/**
 * DemoTopologyDropdown — the topology label in the full-screen scene-identity
 * panel, made selectable. It shows the current pattern and, on click, opens a
 * menu to switch to another (by navigating to its route). Replaces the separate
 * bottom switcher so the only control is one that reads as real product UI.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { useWorldStore } from "../stores/world.js";
import { DEMO_TOPOLOGIES, demoTopologyMeta } from "./topologies.js";

export function DemoTopologyDropdown() {
  const navigate = useNavigate();
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const current = demoTopologyMeta(selectedTopologyId ?? "");

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-sm font-medium text-text-primary transition-colors duration-200 hover:text-white"
      >
        {current.label}
        <ChevronDown
          className={`h-3.5 w-3.5 text-text-muted transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="overlay-panel absolute right-0 top-full z-overlay mt-2 flex w-44 flex-col gap-0.5 rounded-2xl p-1.5 md:left-0 md:right-auto">
          {DEMO_TOPOLOGIES.map((t) => {
            const active = t.id === selectedTopologyId;
            return (
              <button
                key={t.id}
                onClick={() => {
                  navigate(`/topology/${t.id}`);
                  setOpen(false);
                }}
                className={`flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-[color,background-color] duration-150 ${
                  active
                    ? "bg-accent/15 text-text-primary"
                    : "text-text-secondary hover:bg-white/5 hover:text-text-primary"
                }`}
              >
                {t.label}
                {active && <Check className="h-3.5 w-3.5 text-accent-light" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
